import { useState } from 'react'
import { ShieldCheck, ExternalLink, Copy, CheckCircle, AlertCircle } from 'lucide-react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import Modal from './Modal'

interface WebAuthModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  brandName: string
  platform?: string | null
  domain: string
  onSuccess?: () => void
}

const PLATFORM_INSTRUCTIONS = {
  shopify: {
    title: 'Shopify Crawler Access Configuration',
    description: 'Your Shopify store is protected by Cloudflare. To allow BrandIndex AI to crawl your store, you need to generate Crawler Access credentials.',
    steps: [
      'Log in to your Shopify admin panel',
      'Navigate to Sales Channels → Online Store → Preferences',
      'Scroll down to the "Crawler access" section',
      'Click "Create new signature" or "Add crawler access"',
      'Name it "BrandIndex AI" and generate the signature',
      'Copy all three values: Signature, Signature-Input, and Signature-Agent'
    ],
    helpUrl: 'https://help.shopify.com/en/manual/promoting-marketing/seo/crawling-your-store',
    fieldLabel: 'Crawler Access Credentials',
    fieldPlaceholder: 'Paste your credentials below...',
    requiresMultipleFields: true,
    fields: [
      { name: 'signature', label: 'Signature', placeholder: 'sig1=:...=:', required: true },
      { name: 'signatureInput', label: 'Signature-Input', placeholder: 'sig1=("@authority" "signature-agent");...', required: true },
      { name: 'signatureAgent', label: 'Signature-Agent', placeholder: '"https://shopify.com"', required: true }
    ]
  },
  squarespace: {
    title: 'Squarespace API Authentication',
    description: 'Configure API access for crawling your Squarespace site.',
    steps: [
      'Log in to your Squarespace account',
      'Go to Settings → Advanced → API Keys',
      'Click "Generate API Key"',
      'Name it "BrandIndex AI Crawler"',
      'Copy the generated API key'
    ],
    helpUrl: 'https://developers.squarespace.com/api-authentication',
    fieldLabel: 'API Key',
    fieldPlaceholder: 'Paste your Squarespace API key here...'
  },
  wix: {
    title: 'Wix API Authentication',
    description: 'Configure API credentials for crawling your Wix site.',
    steps: [
      'Log in to your Wix account',
      'Go to Settings → Business Info → API Keys',
      'Click "Create New API Key"',
      'Name it "BrandIndex AI Crawler"',
      'Copy the API key and secret'
    ],
    helpUrl: 'https://dev.wix.com/api/rest/getting-started/authentication',
    fieldLabel: 'API Key',
    fieldPlaceholder: 'Paste your Wix API key here...'
  },
  bigcommerce: {
    title: 'BigCommerce API Authentication',
    description: 'Create API credentials to allow crawling of your BigCommerce store.',
    steps: [
      'Log in to your BigCommerce admin',
      'Go to Settings → API → Store-level API accounts',
      'Click "Create API Account"',
      'Name: "BrandIndex AI Crawler"',
      'OAuth Scopes: Select "read-only" for Products and Content',
      'Copy the Access Token'
    ],
    helpUrl: 'https://developer.bigcommerce.com/api-docs/getting-started/authentication',
    fieldLabel: 'API Access Token',
    fieldPlaceholder: 'Paste your BigCommerce access token here...'
  },
  custom: {
    title: 'Custom Web Authentication',
    description: 'Configure custom authentication credentials for crawling your site.',
    steps: [
      'Enter the authentication credentials required by your platform',
      'This could be an API key, bearer token, or custom signature',
      'Contact your platform support if you\'re unsure how to generate credentials'
    ],
    helpUrl: null,
    fieldLabel: 'Authentication Credentials',
    fieldPlaceholder: 'Paste your authentication credentials here...'
  }
}

export default function WebAuthModal({
  isOpen,
  onClose,
  brandId,
  brandName,
  platform,
  domain,
  onSuccess
}: WebAuthModalProps) {
  const [authKey, setAuthKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)

  // Shopify-specific multi-field state
  const [shopifySignature, setShopifySignature] = useState('')
  const [shopifySignatureInput, setShopifySignatureInput] = useState('')
  const [shopifySignatureAgent, setShopifySignatureAgent] = useState('"https://shopify.com"')

  const platformKey = (platform?.toLowerCase() as keyof typeof PLATFORM_INSTRUCTIONS) || 'custom'
  const instructions = PLATFORM_INSTRUCTIONS[platformKey] || PLATFORM_INSTRUCTIONS.custom

  const handleCopyDomain = () => {
    navigator.clipboard.writeText(domain)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSave = async () => {
    // Validate based on platform
    if (platformKey === 'shopify') {
      if (!shopifySignature.trim() || !shopifySignatureInput.trim() || !shopifySignatureAgent.trim()) {
        toast.error('Please enter all three Shopify Crawler Access credentials')
        return
      }
    } else {
      if (!authKey.trim()) {
        toast.error('Please enter authentication credentials')
        return
      }
    }

    try {
      setSaving(true)

      // Build credentials object based on platform
      let credentials
      if (platformKey === 'shopify') {
        credentials = {
          signature: shopifySignature.trim(),
          signatureInput: shopifySignatureInput.trim(),
          signatureAgent: shopifySignatureAgent.trim(),
          platform: platformKey,
          domain: domain,
          configuredAt: new Date().toISOString()
        }
      } else {
        credentials = {
          key: authKey.trim(),
          platform: platformKey,
          domain: domain,
          configuredAt: new Date().toISOString()
        }
      }

      await api.post(`/brands/${brandId}/web-auth`, {
        requiresWebAuth: true,
        webAuthType: platformKey,
        webAuthCredentials: credentials
      })

      toast.success('Web authentication configured successfully!')

      // Reset form
      setAuthKey('')
      setShopifySignature('')
      setShopifySignatureInput('')
      setShopifySignatureAgent('"https://shopify.com"')

      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to save web auth:', error)
      toast.error(error.response?.data?.error || 'Failed to save authentication')
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = () => {
    setAuthKey('')
    setShopifySignature('')
    setShopifySignatureInput('')
    setShopifySignatureAgent('"https://shopify.com"')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" closeOnBackdrop={!saving}>
      <Modal.Header onClose={onClose} showCloseButton={!saving}>
        <ShieldCheck className="h-6 w-6 text-indigo-600 flex-shrink-0" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">{instructions.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {brandName} • {domain}
          </p>
        </div>
      </Modal.Header>
      <Modal.Body className="space-y-6">
          {/* Description */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">{instructions.description}</p>
            </div>
          </div>

          {/* Platform detected */}
          {platform && (
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-600">Detected platform:</span>
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-medium">
                {platform}
              </span>
            </div>
          )}

          {/* Domain info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Domain
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={domain}
                disabled
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              <button
                onClick={handleCopyDomain}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Copy domain"
              >
                {copied ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              How to Generate Authentication Credentials:
            </h3>
            <ol className="space-y-2">
              {instructions.steps.map((step, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-700 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>

            {instructions.helpUrl && (
              <a
                href={instructions.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 mt-4"
              >
                <span>View official documentation</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          {/* Auth credentials input */}
          {platformKey === 'shopify' ? (
            // Shopify: Three separate fields
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature
                </label>
                <textarea
                  value={shopifySignature}
                  onChange={(e) => setShopifySignature(e.target.value)}
                  rows={2}
                  placeholder="sig1=:F7Gprv...."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature-Input
                </label>
                <textarea
                  value={shopifySignatureInput}
                  onChange={(e) => setShopifySignatureInput(e.target.value)}
                  rows={3}
                  placeholder='sig1=("@authority" "signature-agent");keyid="...";nonce="...";tag="web-bot-auth";created=...;expires=...'
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signature-Agent
                </label>
                <input
                  type="text"
                  value={shopifySignatureAgent}
                  onChange={(e) => setShopifySignatureAgent(e.target.value)}
                  placeholder='"https://shopify.com"'
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              <p className="text-xs text-gray-500">
                Copy all three values exactly as shown in your Shopify admin. These credentials will be securely stored and used only for crawling your store.
              </p>
            </div>
          ) : (
            // Other platforms: Single field
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {instructions.fieldLabel}
              </label>
              <textarea
                value={authKey}
                onChange={(e) => setAuthKey(e.target.value)}
                rows={4}
                placeholder={instructions.fieldPlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                This credential will be securely stored and used only for crawling your site.
              </p>
            </div>
          )}

          {/* Security note */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <ShieldCheck className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-600">
                <p className="font-medium mb-1">Security & Privacy</p>
                <p>
                  Your authentication credentials are encrypted and stored securely. They are only
                  used to access public content on your site for analysis purposes.
                </p>
              </div>
            </div>
          </div>
      </Modal.Body>
      <Modal.Footer className="flex justify-between items-center bg-gray-50">
        <button
          onClick={handleSkip}
          disabled={saving}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Skip for now
        </button>
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={
              saving ||
              (platformKey === 'shopify'
                ? !shopifySignature.trim() || !shopifySignatureInput.trim() || !shopifySignatureAgent.trim()
                : !authKey.trim()
              )
            }
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Save Credentials
              </>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
